import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  DollarSign,
  LogOut,
  FileText,
  Menu,
  Package,
  PlusCircle,
  Building2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CreditCard,
  Rss,
  BookOpen,
  Activity,
} from 'lucide-react';

import { useSidebar } from '../../context/SidebarContext';
import { useSidebarData } from '../../context/SidebarDataContext';
import { useAuth } from '../../constants/providers/AuthProvider';
import { supabase } from '../../lib/supabase';
import { useOnboardingStore } from '../../store/onboarding.store';
import { Button } from '../ui/Button';
import { NotificationBell } from '../notifications/NotificationBell';
import { DirectInviteModal } from '../students/DirectInviteModal';

interface SidebarSubItem {
  path: string;
  label: string;
}

interface SidebarItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  isAvailable?: boolean;
  subItems?: SidebarSubItem[];
}

interface SidebarGroup {
  label: string;
  items: SidebarItem[];
}

// Home item - always visible at top
const homeItem: SidebarItem = {
  path: '/dashboard',
  label: 'Dashboard',
  icon: <Home className="w-5 h-5" />,
  isAvailable: true,
};

// Grouped menu items (like mobile drawer)
const sidebarGroups: SidebarGroup[] = [
  {
    label: 'Activity Hub',
    items: [
      {
        path: '/activity',
        label: 'RollOps Pulse',
        icon: <Activity className="w-5 h-5" />,
        isAvailable: true,
      },
      {
        path: '/students',
        label: 'Students',
        icon: <Users className="w-5 h-5" />,
        isAvailable: true,
      },
      {
        path: '/payments',
        label: 'Payments',
        icon: <CreditCard className="w-5 h-5" />,
        isAvailable: true,
      },
    ],
  },
  {
    label: 'Quick Actions',
    items: [
      {
        path: '/students',
        label: 'Students',
        icon: <Users className="w-5 h-5" />,
        isAvailable: true,
        subItems: [
          {
            path: '/students/new',
            label: 'Create Student',
          },
          {
            path: '/students',
            label: 'Search & Manage',
          },
          {
            path: '/upload-student-data',
            label: 'Upload Data',
          },
        ],
      },
      {
        path: '/schedule',
        label: 'Schedule',
        icon: <Calendar className="w-5 h-5" />,
        isAvailable: true,
        subItems: [
          {
            path: '/schedule',
            label: 'View Calendar',
          },
        ],
      },
      {
        path: '/subscriptions-plans/gym',
        label: 'Subscriptions',
        icon: <Package className="w-5 h-5" />,
        isAvailable: true,
        subItems: [
          {
            path: '/subscriptions-plans/gym',
            label: 'Manage Plans',
          },
        ],
      },
    ],
  },
  {
    label: 'Community',
    items: [
      {
        path: '/social',
        label: 'Social Dashboard',
        icon: <Rss className="w-5 h-5" />,
        isAvailable: true,
        subItems: [
          {
            path: '/social',
            label: 'Overview',
          },
          {
            path: '/social/moderation',
            label: 'Moderation',
          },
          {
            path: '/social/analytics',
            label: 'Analytics',
          },
        ],
      },
      {
        path: '/schedule',
        label: 'Schedule',
        icon: <Calendar className="w-5 h-5" />,
        isAvailable: true,
      },
      {
        path: '/notes',
        label: 'Notes',
        icon: <BookOpen className="w-5 h-5" />,
        isAvailable: true,
      },
    ],
  },
  {
    label: 'Documents',
    items: [
      {
        path: '/damage-waivers',
        label: 'Liability Waivers',
        icon: <FileText className="w-5 h-5" />,
        isAvailable: true,
        subItems: [
          {
            path: '/damage-waivers',
            label: 'Manage Waivers',
          },
          {
            path: '/damage-waivers/upload',
            label: 'Upload Waiver',
          },
          {
            path: '/qr-codes',
            label: 'Signup QR Codes',
          },
          {
            path: '/damage-waivers/compliance',
            label: 'Compliance Report',
          },
        ],
      },
    ],
  },
  {
    label: 'Company Settings',
    items: [
      {
        path: '/billing/settings',
        label: 'Billing & Subscriptions',
        icon: <CreditCard className="w-5 h-5" />,
        isAvailable: true,
        subItems: [
          {
            path: '/billing/settings',
            label: 'Billing Settings',
          },
          {
            path: '/subscriptions-plans/gym',
            label: 'Subscription Plans',
          },
          {
            path: '/billing/discount-codes',
            label: 'Discount Codes',
          },
          {
            path: '/subscriptions-plans/rollops',
            label: 'RollOps Subscription',
          },
        ],
      },
      {
        path: '/billing/manual-payments',
        label: 'Billing Operations',
        icon: <DollarSign className="w-5 h-5" />,
        isAvailable: true,
        subItems: [
          {
            path: '/billing/manual-payments',
            label: 'Manual Payments',
          },
          {
            path: '/billing/reports',
            label: 'Billing Reports',
          },
        ],
      },
    ],
  },
];

const scheduleItem: SidebarItem = {
  path: '/schedule',
  label: 'Class Scheduling',
  icon: <Calendar className="w-5 h-5" />,
  isAvailable: false,
};

interface UserCompany {
  company_id: string;
  company_name: string;
  company_logo?: string | null;
  role: string;
}

/**
 * Host wrapper for User navigation.
 * 
 * Provides:
 * - User-specific nav items
 * - Company selector
 * - Onboarding banner
 * - Styling consistent with user theme
 * - Collapse state management
 * - Active route detection
 */
export const UserNavHost = function UserNavHost() {
  const { isOpen, toggle } = useSidebar();
  const {
    companies,
    currentCompany,
    userRole,
    loading,
    expandedItems,
    scrollPosition,
    setExpandedItems,
    setScrollPosition,
    handleSwitchCompany,
  } = useSidebarData();
  const location = useLocation();
  const navigate = useNavigate();
  // Refresh session available for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { refreshSession: _refreshSession } = useAuth();
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [hasIncompleteSetup, setHasIncompleteSetup] = useState(false);
  const [showDirectInviteModal, setShowDirectInviteModal] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const { companyId: onboardingCompanyId, currentSection } = useOnboardingStore();

  // Memoize filtered sidebar groups based on user role to avoid recomputing on every render
  // Use defensive logic to prevent sidebar flash during navigation
  const filteredSidebarGroups = useMemo(() => {
    // Only hide groups if we explicitly know user doesn't have access
    // During navigation/loading, err on the side of showing groups to prevent flash
    if (userRole && !['owner', 'admin'].includes(userRole)) {
      return [];
    }

    // Show all groups by default - only hide when we know user lacks access
    return sidebarGroups.map(group => ({
      ...group,
      items: group.items.filter(() => true) // All items pass role check for owner/admin
    })).filter(group => group.items.length > 0);
  }, [userRole]);

  // Check for incomplete setup (moved from data fetching logic)
  useEffect(() => {
    if (currentCompany) {
      // This logic could be moved to SidebarDataProvider if needed
      const hasOnboardingState = Boolean(onboardingCompanyId && currentSection !== 'complete');
      setHasIncompleteSetup(hasOnboardingState);
    }
  }, [currentCompany, onboardingCompanyId, currentSection]);

  // Restore scroll position
  useEffect(() => {
    if (scrollRef.current && scrollPosition > 0) {
      scrollRef.current.scrollTop = scrollPosition;
    }
  }, [scrollPosition]);

  // Auto-expand menu items when on sub-pages
  useEffect(() => {
    if (!userRole || !location.pathname) return;

    const currentPath = location.pathname;
    const itemsToExpand = new Set<string>();

    // Check sidebar groups for expandable items
    sidebarGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (item.subItems) {
          const isOnSubPage = item.subItems.some(sub =>
            currentPath === sub.path || currentPath.startsWith(sub.path + '/')
          );
          if (isOnSubPage) {
            itemsToExpand.add(item.path);
          }
        }
      });
    });

    if (itemsToExpand.size > 0) {
      const next = new Set([...expandedItems, ...itemsToExpand]);
      setExpandedItems(next);
    }
  }, [location.pathname, userRole, expandedItems, setExpandedItems]);


  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate('/');
  }, [navigate]);

  // Header with logo and toggle
  const header = (
    <div className="p-4 border-b border-[#504A4A]">
      {isOpen ? (
        <div className="flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center space-x-3 flex-1 min-w-0">
            <img 
              src="/rollops-logo.png" 
              alt="RollOps Logo" 
              width={32} 
              height={32} 
              className="w-8 h-8 flex-shrink-0" 
            />
            <span className="text-xl font-bold transition-all duration-300 text-[#EBFFEE]">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
          </Link>
          <div className="flex items-center gap-2 flex-shrink-0">
            <NotificationBell />
            <button
              onClick={toggle}
              className="w-8 h-8 flex items-center justify-center text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={toggle}
            className="w-8 h-8 flex items-center justify-center text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>
          <NotificationBell />
        </div>
      )}
    </div>
  );

  // Company selector (only when open)
  const companySelector = isOpen && !loading && companies.length > 0 && (
    <div className="px-3 py-3 border-b border-[#504A4A]">
      <div className="relative">
        <button
          onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
          className="w-full flex items-center gap-3 p-2 rounded-lg bg-[#363232] hover:bg-[#504A4A] transition-colors"
        >
          {currentCompany?.company_logo ? (
            <img
              src={currentCompany.company_logo}
              alt={currentCompany.company_name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
          )}
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-medium text-[#EBFFEE] truncate">
              {currentCompany?.company_name || 'Select Gym'}
            </p>
            <p className="text-xs text-[#A9A3A3] truncate">
              {currentCompany?.role}
            </p>
          </div>
          <ChevronDown 
            className={`w-4 h-4 text-[#A9A3A3] transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} 
          />
        </button>

        {showCompanyDropdown && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-[#363232] border border-[#504A4A] rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto scrollbar-styled">
            {companies.map((company: UserCompany) => (
              <button
                key={company.company_id}
                onClick={() => handleSwitchCompany(company as UserCompany)}
                className={`w-full flex items-center gap-3 p-3 hover:bg-[#504A4A] transition-colors ${
                  currentCompany?.company_id === company.company_id ? 'bg-[#504A4A]' : ''
                }`}
              >
                {company.company_logo ? (
                  <img
                    src={company.company_logo}
                    alt={company.company_name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-[#EBFFEE] truncate">
                    {company.company_name}
                  </p>
                  <p className="text-xs text-[#A9A3A3] truncate">
                    {company.role}
                  </p>
                </div>
                {currentCompany?.company_id === company.company_id && (
                  <div className="w-2 h-2 rounded-full bg-[#9B1421]" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Onboarding banner
  const onboardingBanner = hasIncompleteSetup && isOpen && (
    <div className="px-3 py-3 border-b border-[#504A4A]">
      <Link
        to="/onboarding"
        className="flex items-center gap-3 p-3 rounded-lg bg-[#9B1421]/20 border border-[#9B1421]/50 hover:bg-[#9B1421]/30 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#EBFFEE]">Resume Setup</p>
          <p className="text-xs text-[#A9A3A3] truncate">Complete your gym setup</p>
        </div>
      </Link>
    </div>
  );

  // Footer with create gym, schedule, and sign out
  const footer = (
    <>
        {/* Schedule item */}
      <div className="border-t border-[#504A4A]">
        <div className={`${isOpen ? 'px-3 py-2' : 'px-2 py-2'}`}>
          <div
            className={`
              flex items-center cursor-not-allowed text-[#706A6A] transition-all duration-200 py-2 rounded-lg relative
            `}
            title={`${scheduleItem.label} - Coming Soon`}
          >
            <span className={`
              flex-shrink-0 w-5 h-5 flex items-center justify-center
              transition-all duration-300
            `}
            style={{ marginRight: isOpen ? '8px' : '0px' }}
            >
              {scheduleItem.icon}
            </span>
            {isOpen && (
              <span className="text-sm font-medium transition-all duration-300 whitespace-nowrap">
                {scheduleItem.label}
                <span className="text-xs ml-2 text-[#504A4A]">(Soon)</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Create Gym + Sign Out */}
      <div className="border-t border-[#504A4A]">
        <div className={`${isOpen ? 'px-3 py-2' : 'px-2 py-2 flex justify-center'}`}>
          <span
            className={`
              flex items-center text-[#A9A3A3] opacity-60 cursor-not-allowed
              ${isOpen ? 'gap-2' : ''}
            `}
            title={!isOpen ? 'Create New Gym' : undefined}
            aria-disabled="true"
          >
            <PlusCircle className="w-4 h-4" />
            {isOpen && (
              <span className="text-xs">Create New Gym</span>
            )}
          </span>
        </div>

        <div className={`${isOpen ? 'px-3 py-3' : 'px-2 py-3 flex justify-center'}`}>
          <Button
            onClick={handleSignOut}
            className={`
              bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] transition-all duration-300 flex items-center py-2 rounded-lg relative
              ${isOpen 
                ? 'w-full justify-center' 
                : 'w-10 h-10'
              }
            `}
            title={!isOpen ? 'Sign Out' : undefined}
          >
            <span 
              className={`
                flex-shrink-0 w-4 h-4 flex items-center justify-center absolute top-1/2 -translate-y-1/2
                transition-all duration-300
                ${isOpen 
                  ? 'left-3' 
                  : 'left-1/2 -translate-x-1/2'
                }
              `}
            >
              <LogOut className="w-4 h-4" />
            </span>
            {isOpen && (
              <span className="text-sm transition-all duration-300 whitespace-nowrap ml-6">
                Sign Out
              </span>
            )}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <aside
      className={`
        fixed left-0 top-0 h-full bg-[#221F1F] border-r border-[#504A4A] z-40
        transition-all duration-300 ease-in-out font-['Inter']
        ${isOpen ? 'w-64 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'}
        lg:top-0 lg:h-full
        top-14 h-[calc(100vh-3.5rem)]
      `}
      style={{
        // Prevent content shift during transitions
        contain: 'layout style paint',
        willChange: 'transform, width'
      }}
    >
      <div className="flex flex-col h-full">
        {header}
        {companySelector}
        {onboardingBanner}
        
        {/* Collapsed company indicator */}
        {!isOpen && !loading && currentCompany && (
          <div className="py-3 px-2 border-b border-[#504A4A] flex justify-center">
            <div 
              className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center cursor-pointer"
              title={currentCompany.company_name}
              onClick={toggle}
            >
              {currentCompany.company_logo ? (
                <img
                  src={currentCompany.company_logo}
                  alt={currentCompany.company_name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <Building2 className="w-4 h-4 text-white" />
              )}
            </div>
          </div>
        )}

        {/* Collapsed Resume Setup indicator */}
        {hasIncompleteSetup && !isOpen && (
          <div className="py-3 px-2 border-b border-[#504A4A] flex justify-center">
            <Link
              to="/onboarding"
              className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center"
              title="Resume Setup"
            >
              <AlertCircle className="w-4 h-4 text-white" />
            </Link>
          </div>
        )}

        {/* Navigation Items */}
        <nav
          ref={scrollRef}
          className="flex-1 py-4 overflow-y-auto scrollbar-styled"
          style={{ minHeight: 0 }}
          onScroll={(e) => setScrollPosition(e.currentTarget.scrollTop)}
        >
          <ul className={`space-y-2 ${isOpen ? 'px-3' : 'px-2'}`}>
            {/* Home Item - Always visible */}
            {homeItem.isAvailable && (
              <li>
                <Link
                  to={homeItem.path}
                  className={`
                    flex items-center transition-all duration-200 py-2 rounded-lg relative
                    ${location.pathname === homeItem.path || location.pathname === '/'
                      ? 'bg-[#9B1421] text-[#EBFFEE]' 
                      : 'text-[#A9A3A3] hover:bg-[#363232] hover:text-[#EBFFEE]'
                    }
                    ${isOpen ? 'gap-2' : ''}
                  `}
                  title={!isOpen ? homeItem.label : undefined}
                  style={{
                    paddingLeft: isOpen ? '12px' : '14px',
                    gap: isOpen ? '8px' : '0px',
                  }}
                >
                  <span 
                    className="flex-shrink-0 transition-all duration-300"
                    style={{
                      display: 'inline-block',
                      marginRight: isOpen ? '8px' : '0px',
                    }}
                  >
                    {homeItem.icon}
                  </span>
                  <span 
                    className={`
                      text-sm font-medium transition-all duration-300 whitespace-nowrap
                      ${isOpen 
                        ? 'opacity-100 max-w-full' 
                        : 'opacity-0 max-w-0 overflow-hidden'
                      }
                    `}
                  >
                    {homeItem.label}
                  </span>
                </Link>
              </li>
            )}

            {/* Grouped Menu Items - Only show when not loading to prevent flash */}
            {!loading && filteredSidebarGroups.map((group) => {

              return (
                <React.Fragment key={group.label}>
                  {/* Group Label - Always show when sidebar is open */}
                  {isOpen && (
                    <li className="pt-4 pb-2 first:pt-0" data-group-label={group.label}>
                      <span className="text-xs font-semibold text-[#706A6A] uppercase tracking-wider px-3 block">
                        {group.label}
                      </span>
                    </li>
                  )}
                  
                  {/* Group Items */}
                  {group.items.map((item) => {
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isExpanded = expandedItems.has(item.path);
                    const isActive = location.pathname === item.path || 
                      (item.path === '/billing' ? location.pathname.startsWith('/billing') : 
                       hasSubItems ? item.subItems?.some(sub => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')) :
                       location.pathname.startsWith(item.path + '/'));
                    
                    const toggleExpand = (e: React.MouseEvent) => {
                      e.preventDefault();
                      if (hasSubItems && isOpen) {
                        const next = new Set(expandedItems);
                        if (next.has(item.path)) {
                          next.delete(item.path);
                        } else {
                          next.add(item.path);
                        }
                        setExpandedItems(next);
                      }
                    };
                    
                    return (
                      <li key={item.path}>
                        {item.isAvailable ? (
                          <>
                            {hasSubItems ? (
                              <button
                                onClick={toggleExpand}
                                className={`
                                  w-full flex items-center transition-all duration-200 py-2 rounded-lg relative
                                  ${isActive 
                                    ? 'bg-[#9B1421] text-[#EBFFEE]' 
                                    : 'text-[#A9A3A3] hover:bg-[#363232] hover:text-[#EBFFEE]'
                                  }
                                  ${isOpen ? 'gap-2' : ''}
                                `}
                                title={!isOpen ? item.label : undefined}
                                style={{
                                  paddingLeft: isOpen ? '12px' : '14px',
                                  gap: isOpen ? '8px' : '0px',
                                }}
                              >
                                <span 
                                  className="flex-shrink-0 transition-all duration-300"
                                  style={{
                                    display: 'inline-block',
                                    marginRight: isOpen ? '8px' : '0px',
                                  }}
                                >
                                  {item.icon}
                                </span>
                                <span 
                                  className={`
                                    text-sm font-medium transition-all duration-300 whitespace-nowrap flex-1 text-left
                                    ${isOpen 
                                      ? 'opacity-100 max-w-full' 
                                      : 'opacity-0 max-w-0 overflow-hidden'
                                    }
                                  `}
                                >
                                  {item.label}
                                </span>
                                {isOpen && (
                                  <ChevronRight 
                                    className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                  />
                                )}
                              </button>
                            ) : (
                              <Link
                                to={item.path}
                                className={`
                                  flex items-center transition-all duration-200 py-2 rounded-lg relative
                                  ${isActive 
                                    ? 'bg-[#9B1421] text-[#EBFFEE]' 
                                    : 'text-[#A9A3A3] hover:bg-[#363232] hover:text-[#EBFFEE]'
                                  }
                                  ${isOpen ? 'gap-2' : ''}
                                `}
                                title={!isOpen ? item.label : undefined}
                                style={{
                                  paddingLeft: isOpen ? '12px' : '14px',
                                  gap: isOpen ? '8px' : '0px',
                                }}
                              >
                                <span 
                                  className="flex-shrink-0 transition-all duration-300"
                                  style={{
                                    display: 'inline-block',
                                    marginRight: isOpen ? '8px' : '0px',
                                  }}
                                >
                                  {item.icon}
                                </span>
                                <span 
                                  className={`
                                    text-sm font-medium transition-all duration-300 whitespace-nowrap
                                    ${isOpen 
                                      ? 'opacity-100 max-w-full' 
                                      : 'opacity-0 max-w-0 overflow-hidden'
                                    }
                                  `}
                                >
                                  {item.label}
                                </span>
                              </Link>
                            )}
                            {/* Sub-items */}
                            {hasSubItems && isExpanded && isOpen && (
                              <ul className="ml-4 mt-1 space-y-1 border-l border-[#504A4A] pl-2">
                                {item.subItems?.map((subItem) => {
                                  const isSubActive = location.pathname === subItem.path || location.pathname.startsWith(subItem.path + '/');
                                  // Special handling for "Create Student" - open modal instead of navigating (no route exists)
                                  const isCreateStudent = subItem.path === '/students/new';
                                  if (isCreateStudent) {
                                    return (
                                      <li key={subItem.path}>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.nativeEvent.stopImmediatePropagation();
                                            setShowDirectInviteModal(true);
                                          }}
                                          className={`
                                            w-full flex items-center py-1.5 px-3 rounded-lg transition-all duration-200 text-sm text-left
                                            text-text-secondary hover:bg-[#363232] hover:text-text-primary
                                          `}
                                        >
                                          <span className="text-xs mr-2">•</span>
                                          {subItem.label}
                                        </button>
                                      </li>
                                    );
                                  }
                                  return (
                                    <li key={subItem.path}>
                                      <Link
                                        to={subItem.path}
                                        className={`
                                          flex items-center py-1.5 px-3 rounded-lg transition-all duration-200 text-sm
                                          ${isSubActive 
                                            ? 'bg-[#9B1421]/50 text-text-primary' 
                                            : 'text-text-secondary hover:bg-[#363232] hover:text-text-primary'
                                          }
                                        `}
                                      >
                                        <span className="text-xs mr-2">•</span>
                                        {subItem.label}
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </>
                        ) : (
                          <div
                            className={`
                              flex items-center cursor-not-allowed text-[#706A6A] transition-all duration-200 py-2 rounded-lg relative
                              ${isOpen ? 'gap-2' : ''}
                            `}
                            title={`${item.label} - Coming Soon`}
                            style={{
                              paddingLeft: isOpen ? '12px' : '14px',
                              gap: isOpen ? '8px' : '0px',
                            }}
                          >
                            <span 
                              className="flex-shrink-0 transition-all duration-300"
                              style={{
                                display: 'inline-block',
                                marginRight: isOpen ? '8px' : '0px',
                              }}
                            >
                              {item.icon}
                            </span>
                            <span 
                              className={`
                                text-sm font-medium transition-all duration-300 whitespace-nowrap
                                ${isOpen 
                                  ? 'opacity-100 max-w-full' 
                                  : 'opacity-0 max-w-0 overflow-hidden'
                                }
                              `}
                            >
                              {item.label}
                              <span className="text-xs ml-2 text-[#504A4A]">(Soon)</span>
                            </span>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </ul>
        </nav>

        {/* Footer Section */}
        {footer}
      </div>
      
      {/* Direct Invite Modal */}
      <DirectInviteModal
        isOpen={showDirectInviteModal}
        onClose={() => setShowDirectInviteModal(false)}
      />
    </aside>
  );
};
